import IconClipboard from '@apollo/icons/default/IconClipboard.svg';
import IconDownload from '@apollo/icons/default/IconDownload.svg';
import { IconButton, customColors } from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import { diffJson } from 'diff';
import { saveAs } from 'file-saver';
import { ExecutionResult, printError } from 'graphql';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { makeRangeFromOffset } from 'src/app/graph/explorerPage/helpers/monacoHelpers';
import { useDerivedEditorValue } from 'src/app/graph/explorerPage/hooks/useDerivedEditorValue';
import { useInlineClassRangesEffect } from 'src/app/graph/explorerPage/hooks/useInlineClassRangesEffect';
import { DotsTimeline } from 'src/components/charts/DotsTimeline';
import { useCursorDateIndex } from 'src/components/charts/helpers/useCursorDateIndex';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ReadonlyEditor } from 'src/components/editor/ReadonlyEditor';
import { useShowToast } from 'src/components/toast/Toast';
import {
  MONACO_READONLY_OPTIONS,
  StudioEditorContainer,
  getFileOverFormattingThreshold,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { ExplorerBehavioralFlags } from '../../explorerBehavioralFlags';
import { JSONObject } from '../../helpers/types';
import {
  ExplorerError,
  ExplorerResponse,
} from '../../hooks/useExplorerState/useExplorerResponses';

import { JSONTableView } from './jsonTableView/JSONTableView';

const { bg } = customColors;

const prettyPrintJson = (data: ExecutionResult) => {
  return JSON.stringify(data, null, '  ');
};

export const ResponsePane = ({
  lastResult,
  isTableMode,
  responseName,
  explorerBehavioralFlags,
}: {
  lastResult: ExplorerResponse | ExplorerError | undefined;
  isTableMode: boolean;
  responseName: string | undefined;
  explorerBehavioralFlags: ExplorerBehavioralFlags;
}) => {
  const { showToasts } = useShowToast();
  const data = lastResult?.type === 'response' ? lastResult.data : null;
  const getMaxDate = useCallback(
    (maxDate: Date) => {
      if (lastResult?.type === 'response' && lastResult.data.hasNext) {
        return { date: new Date(), poll: true };
      } else {
        return { date: maxDate, poll: false };
      }
    },
    [lastResult],
  );
  const historyIndex = useCursorDateIndex({
    data: data?.history,
    getTimestamp: (bucket: { timestamp: Timestamp['output'] }) =>
      bucket.timestamp,
  });

  const currentHistoryData =
    historyIndex !== null ? data?.history?.[historyIndex]?.data : data?.body;

  const editorDefaultValue = useMemo(() => {
    return currentHistoryData
      ? prettyPrintJson(currentHistoryData)
      : (data?.bodyText ?? '');
  }, [currentHistoryData, data?.bodyText]);

  const fileOverFormattingThreshold =
    getFileOverFormattingThreshold(editorDefaultValue);
  useEffect(() => {
    if (fileOverFormattingThreshold) {
      showToasts({
        stableId: 'large-explorer-response',
        heading: 'Very large response',
        level: 'info',
        message:
          'The response for this operation is very large. For better performance, we have disabled word wrapping and tokenization in the response preview.',
      });
    }
  }, [showToasts, fileOverFormattingThreshold]);
  const editor = useStudioEditor({
    language: data?.body ? 'json' : 'text',
    defaultValue: editorDefaultValue,
    showFoldingControls: 'always',
    ...MONACO_READONLY_OPTIONS,
  });

  React.useEffect(() => {
    // When contents change in a read only editor, reset selection
    const disposable = editor.onDidChangeModelContent(() =>
      editor.setSelection(new monacoEditor.Range(1, 1, 1, 1)),
    );
    return () => disposable.dispose();
  }, [editor]);

  const diffJsonMemoed = useMemo(
    () => _.memoize(diffJson, (base, diff) => `${base}${diff}`),
    [],
  );
  const historyLengthRef = useRef(data?.history?.length ?? 0);
  useEffect(() => {
    // when a new response, clear the cache to avoid any memory leaks
    const currentHistoryLength = data?.history?.length;
    if (
      typeof currentHistoryLength === 'number' &&
      historyLengthRef.current > currentHistoryLength
    ) {
      diffJsonMemoed.cache.clear();
    }
    historyLengthRef.current = currentHistoryLength ?? 0;
  }, [data?.history?.length, diffJsonMemoed.cache]);
  const diffRanges = useDerivedEditorValue({
    editor,
    getValue: useCallback(
      (currentText: string) => {
        const model = editor.getModel();
        if (!model || !data?.history || historyIndex === null) {
          return [];
        }

        let offset = 0;
        return diffJsonMemoed(
          currentText,
          prettyPrintJson(data.history[historyIndex - 1]?.data ?? {}),
        ).flatMap((diff) => {
          const lastOffset = offset;
          offset += diff.value.length;
          if (diff.added || diff.removed) {
            return [];
          } else {
            return makeRangeFromOffset(lastOffset, model, diff.value.length);
          }
        });
      },
      [data?.history, diffJsonMemoed, editor, historyIndex],
    ),
    withPosition: false,
  });

  useInlineClassRangesEffect({
    className: 'opacity-50',
    editor,
    ranges: diffRanges,
    skip: !isMinLengthArray(2, data?.history),
  });
  const dotTimelineStack = useMemo(() => {
    return {
      getValue: ({ timestamp }: { timestamp: number }) => ({ timestamp }),
      primaryColor: bg.info,
      data: data?.history ?? [],
    };
  }, [data?.history]);

  const tableViewData = useMemo((): JSONObject | undefined => {
    if (
      isTableMode &&
      lastResult &&
      lastResult.type === 'response' &&
      currentHistoryData?.data
    ) {
      const { errors, ...dataRest } = currentHistoryData;
      return {
        // TODO: type assertion added when migrating to typescript 5.0.4, it got
        // much stricter about requiring index definitions on objects to match JSONObjet. We should remove it
        ...(dataRest as JSONObject),
        ...(errors ? { errors: errors.map(printError) } : {}),
      };
    }
  }, [currentHistoryData, isTableMode, lastResult]);

  return (
    <div className="relative flex h-full grow flex-col">
      {lastResult && (
        <div className="absolute flex size-full flex-1 flex-col overflow-hidden">
          {tableViewData ? (
            <JSONTableView json={tableViewData} className="h-0 flex-1" />
          ) : (
            <>
              {lastResult.type === 'response' ? (
                <StudioEditorContainer editor={editor} className="h-0 flex-1" />
              ) : (
                <ReadonlyEditor
                  language="text"
                  src={JSON.stringify(_.omit(lastResult, 'type'), null, '  ')}
                  monacoOptions={{
                    showFoldingControls: 'always',
                  }}
                />
              )}

              {lastResult.type === 'response' &&
                explorerBehavioralFlags.showResponseActions && (
                  <div className="absolute right-0 top-2 z-50 flex gap-1">
                    <Tooltip label="Copy response">
                      <IconButton
                        aria-label="Copy response to clipboard"
                        size="sm"
                        variant="hidden"
                        onClick={() =>
                          copy(
                            lastResult.data.body
                              ? prettyPrintJson(lastResult.data.body)
                              : lastResult.data.bodyText,
                          )
                        }
                        icon={<IconClipboard />}
                      />
                    </Tooltip>
                    <Tooltip label="Download response">
                      <IconButton
                        aria-label="Download response"
                        size="sm"
                        variant="hidden"
                        onClick={() => {
                          saveAs(
                            new Blob(
                              [
                                lastResult.data.body
                                  ? JSON.stringify(lastResult.data.body)
                                  : lastResult.data.bodyText,
                              ],
                              { type: 'text/json;charset=utf-8' },
                            ),
                            `${responseName || 'response'}.json`,
                          );
                        }}
                        icon={<IconDownload />}
                      />
                    </Tooltip>
                  </div>
                )}
            </>
          )}

          {lastResult.type === 'response' &&
            (isMinLengthArray(2, data?.history) || lastResult.data.hasNext) && (
              <div className="mx-5 mb-7 mt-4 flex-none">
                <div className="mb-5 text-base font-semibold text-primary">
                  Response Timeline
                </div>
                <DotsTimeline
                  key={lastResult.runId}
                  getMaxDate={getMaxDate}
                  className="flex-none"
                  stack={dotTimelineStack}
                  minDate={lastResult.data.startTime}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};
